import * as moment from "moment";
/** Maximum number of records submitted in a single import request. */
export const MAX_IMPORT_RECORDS = 2000;

export type LTAFieldKey =
  | "ContractTitle"
  | "ContractorName"
  | "ContractNo"
  | "CeilingContractValue"
  | "StartDate"
  | "EndDate"
  | "ContractType"
  | "Agency"
  | "Location"
  | "isLTAUsableByOtherAgencies"
  | "AdditionalInformation";

export type FieldMapping = Partial<Record<LTAFieldKey, string>>;

export type ImportStatus = "idle" | "running" | "done";

export type ImportResults = {
  total: number;
  successful: number;
  failed: number;
  downloadUrl: string | null;
};

export const LTA_FIELDS: Array<{
  key: LTAFieldKey;
  label: string;
  required?: boolean;
}> = [
  { key: "ContractTitle", label: "Contract Title", required: true },
  { key: "ContractorName", label: "Contractor", required: true },
  { key: "ContractNo", label: "Contract No", required: true },
  {
    key: "CeilingContractValue",
    label: "Ceiling Contract Value",
    required: true,
  },
  { key: "StartDate", label: "Start Date" },
  { key: "EndDate", label: "End Date", required: true },
  { key: "ContractType", label: "Contract Type", required: true },
  { key: "Agency", label: "Agency", required: true },
  { key: "Location", label: "Location" },
  {
    key: "isLTAUsableByOtherAgencies",
    label: "Is LTA Usable by Other Agencies",
  },
  { key: "AdditionalInformation", label: "Additional Information" },
];

function normalizeStr(s: string): string {
  return s
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");
}

function similarityScore(a: string, b: string): number {
  if (a === b) return 2;
  if (a.includes(b) || b.includes(a)) {
    return Math.min(a.length, b.length) / Math.max(a.length, b.length);
  }
  return 0;
}

export function autoMapFields(csvHeaders: string[]): FieldMapping {
  const mapping: FieldMapping = {};
  const claimed = new Set<string>();

  for (const field of LTA_FIELDS) {
    const normField = normalizeStr(field.key);
    let bestHeader = "";
    let bestScore = 0;

    for (const header of csvHeaders) {
      if (claimed.has(header)) continue;
      const score = similarityScore(normField, normalizeStr(header));
      if (score > bestScore) {
        bestScore = score;
        bestHeader = header;
      }
    }

    if (bestScore > 0) {
      mapping[field.key] = bestHeader;
      claimed.add(bestHeader);
    }
  }

  return mapping;
}

export function mapRowToRecord(
  row: Record<string, string>,
  mappings: FieldMapping,
) {
  const get = (key: LTAFieldKey): string | null => {
    const col = mappings[key];
    if (!col) return null;
    return row[col]?.trim() || null;
  };

  return {
    contractTitle: get("ContractTitle") ?? "",
    contractor: get("ContractorName"),
    contractNo: get("ContractNo") ?? "",
    contractValue: get("CeilingContractValue"),
    startDate:
      (get("StartDate") &&
        moment(get("StartDate"), "DD/MM/YYYY")
          .toDate()
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    endDate:
      (get("EndDate") &&
        moment(get("EndDate"), "DD/MM/YYYY")
          .toDate()
          .toLocaleDateString("en-CA")) ||
      null,
    contractType: get("ContractType"),
    agency: get("Agency"),
    location: get("Location"),
    isLTA: true,
    isLTAUsableByOtherAgencies:
      get("isLTAUsableByOtherAgencies") === "Yes" ? true : false,
    additionalInformation: get("AdditionalInformation"),
  };
}
